import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { DashboardService } from '../services/dashboardService';
import { DashboardTab, SaveUserPreferencesRequest, UserDashboardItem } from '../types/DashboardRequests';
import { Dashboard } from '../types/Dashboard';
import { DashboardLayout, LayoutSlot } from '../types/Layout';
import { SUGGESTED_LAYOUTS } from '../data/suggestedLayouts';
import { useToast } from '@/shared/hooks/use-toast';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';

const PREFERENCES_KEY_BASE = 'dashboard-preferences';

export interface UseDashboardTabsResult {
  tabs: DashboardTab[];
  currentTabId: string | undefined;
  currentTab: DashboardTab | undefined;
  isLoading: boolean;
  /** True while a tab mutation (switch/add/rename/delete) is in flight. */
  isSwitching: boolean;
  /** Has the user ever opted into tabs? When false, the tab bar stays hidden. */
  hasTabs: boolean;
  setActiveTab: (tabId: string) => Promise<void>;
  /**
   * First-time bootstrap: wraps the current root-level prefs into a "Principal"
   * tab and creates a new empty tab. After bootstrap, subsequent calls just add
   * an empty tab. Returns the new tab's id (the empty one).
   */
  addTab: (opts?: { name?: string }) => Promise<string | undefined>;
  renameTab: (tabId: string, newName: string) => Promise<void>;
  deleteTab: (tabId: string) => Promise<void>;
  duplicateTab: (tabId: string, newName?: string) => Promise<string | undefined>;
}

export const useDashboardTabs = (): UseDashboardTabsResult => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  // PBI #78540: scope context drives both READ (cascade resolver) and WRITE
  // (byScope slot). See useDashboardLayouts for the same pattern. orgId is
  // resolved from `selectedOrganization` first so a superadmin viewing a
  // different org's dashboards reads/writes against THAT org's slot, not
  // their primary one from session.
  const { scope: viewScope } = useViewScope();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId ?? undefined;
  const saveContext = {
    scope: viewScope,
    workspaceId: viewScope === 'product' ? currentWorkspace?.id : undefined,
    orgId: viewScope === 'organization' || viewScope === 'product' ? effectiveOrgId : undefined,
  };
  const PREFERENCES_KEY = [
    PREFERENCES_KEY_BASE,
    viewScope,
    saveContext.orgId ?? null,
    saveContext.workspaceId ?? null,
  ];

  const { data: preferences, isLoading } = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => {
      const backendScope = viewScope === 'platform' ? 'superadmin' : viewScope;
      if (backendScope) {
        const result = await DashboardService.getUserPreferencesWithSource({
          scope: backendScope,
          orgId: saveContext.orgId,
          productId: saveContext.workspaceId,
        });
        return result.data;
      }
      return DashboardService.getUserPreferences();
    },
    staleTime: 5 * 60 * 1000,
  });

  const tabs = useMemo<DashboardTab[]>(() => preferences?.tabs ?? [], [preferences]);
  const currentTabId = preferences?.currentTabId;
  const currentTab = useMemo<DashboardTab | undefined>(() => {
    if (!tabs.length) return undefined;
    return (currentTabId && tabs.find((x) => x.id === currentTabId)) || tabs[0];
  }, [tabs, currentTabId]);

  const hasTabs = tabs.length > 0;

  const savePrefsMutation = useMutation({
    mutationFn: (patch: SaveUserPreferencesRequest) =>
      DashboardService.saveUserPreferences(patch, saveContext),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<typeof preferences>(PREFERENCES_KEY);

      queryClient.setQueryData<typeof preferences>(PREFERENCES_KEY, (old) => {
        if (!old) return old;
        const next = { ...old };
        if (patch.tabs !== undefined) next.tabs = patch.tabs;
        if (patch.currentTabId !== undefined) next.currentTabId = patch.currentTabId ?? undefined;
        // When the patch implies a tab switch, also mirror the new active tab's
        // slice into the root fields so consumers reading root fields update
        // immediately (instead of waiting for the network round-trip).
        if (next.tabs && next.tabs.length > 0) {
          const activeId = next.currentTabId ?? next.tabs[0].id;
          const active = next.tabs.find((x) => x.id === activeId) ?? next.tabs[0];
          next.items = active.items ?? [];
          next.gridColumns = active.gridColumns ?? next.gridColumns ?? 2;
          next.layouts = active.layouts ?? [];
          next.currentLayoutId = active.currentLayoutId;
          next.currentTabId = active.id;
        }
        return next;
      });

      // No dashboards-cache manipulation needed: useDashboards keys its
      // query by tabId, so each tab owns its own cache. Switching back to
      // a previously visited tab returns its cached dashboards immediately
      // while a background revalidation runs.
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREFERENCES_KEY, context.previous);
      }
      toast({
        title: t('dashboards.tabs.saveError', 'Erro ao salvar abas'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // Invalidate all per-tab dashboards caches so they revalidate next
      // time each is rendered. The currently-active tab will refetch right
      // away (it's the mounted query); other tabs' caches are marked stale
      // and refetch when the user navigates back to them.
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });

  const defaultMainName = useCallback(() => {
    return i18n.language?.startsWith('pt') ? 'Principal' : 'Main';
  }, [i18n.language]);

  const defaultNewName = useCallback(() => {
    return i18n.language?.startsWith('pt') ? 'Nova aba' : 'New tab';
  }, [i18n.language]);

  const setActiveTab = useCallback(
    async (tabId: string): Promise<void> => {
      if (tabId === currentTab?.id) return;
      await savePrefsMutation.mutateAsync({ currentTabId: tabId });
    },
    [currentTab, savePrefsMutation]
  );

  /**
   * Best-effort snapshot of dashboards currently visible to the user, for the
   * bootstrap "Principal" tab. In legacy mode `preferences.items` only holds
   * the user's explicit overrides — org defaults appear auto-merged at read
   * time without being in items[]. To preserve what the user sees, we capture
   * the dashboards list from the React Query cache (`['dashboards', ...]`)
   * and convert it to items. If no cache entry is found yet, falls back to
   * preferences.items.
   */
  const captureCurrentVisibleItems = useCallback((): UserDashboardItem[] => {
    const cached = queryClient.getQueriesData<Dashboard[]>({ queryKey: ['dashboards'] });
    // Prefer the most recently used entry (React Query exposes them in insertion order;
    // we just pick the first array we find — there's usually only one active at a time).
    const dashboards = cached.find(([, data]) => Array.isArray(data))?.[1];
    if (!dashboards || dashboards.length === 0) {
      return preferences?.items ?? [];
    }
    return dashboards.map((d, index) => ({
      id: d.user_dashboard_id || d.id,
      dashboardBaseId: d.id,
      position: d.position ?? index,
      size: d.size,
      filters: d.filters,
      visible: d.is_active ?? true,
      customName: d.customName,
    }));
  }, [queryClient, preferences]);

  /**
   * Builds a fresh editable layout seeded with the KPIs em destaque template
   * slots. Each slot gets a new UUID so it doesn't collide with the template
   * or other layouts. Used when creating a new tab so the user always lands
   * in a structured, editable arrangement.
   */
  const buildDefaultLayoutForNewTab = useCallback((): DashboardLayout => {
    const template = SUGGESTED_LAYOUTS.find((l) => l.id === 'suggested-kpi-heavy');
    const seed: LayoutSlot[] =
      template?.slots.map((s) => ({ ...s, id: uuidv4() })) ?? [];
    const now = new Date().toISOString();
    return {
      id: uuidv4(),
      name: t('dashboards.layouts.customName', 'Personalizado'),
      slots: seed,
      assignments: [],
      isSuggested: false,
      createdAt: now,
      updatedAt: now,
    };
  }, [t]);

  const addTab = useCallback(
    async (opts?: { name?: string }): Promise<string | undefined> => {
      const now = new Date().toISOString();
      const newTabId = uuidv4();
      const seededLayout = buildDefaultLayoutForNewTab();
      const newTab: DashboardTab = {
        id: newTabId,
        name: opts?.name ?? defaultNewName(),
        items: [],
        gridColumns: preferences?.gridColumns ?? 2,
        layouts: [seededLayout],
        currentLayoutId: seededLayout.id,
        createdAt: now,
        updatedAt: now,
      };

      let nextTabs: DashboardTab[];
      if (tabs.length === 0) {
        // First-time bootstrap: wrap the CURRENT VISIBLE state (legacy auto-
        // merged defaults + user items) into a "Principal" tab + new tab seeded
        // with the default KPIs layout. Principal keeps the user's existing
        // arrangement (suggested or custom) — the simplification only applies
        // forward.
        const mainTab: DashboardTab = {
          id: uuidv4(),
          name: defaultMainName(),
          items: captureCurrentVisibleItems(),
          gridColumns: preferences?.gridColumns ?? 2,
          layouts: preferences?.layouts ?? [],
          currentLayoutId: preferences?.currentLayoutId,
          createdAt: now,
          updatedAt: now,
        };
        nextTabs = [mainTab, newTab];
      } else {
        nextTabs = [...tabs, newTab];
      }

      await savePrefsMutation.mutateAsync({ tabs: nextTabs, currentTabId: newTabId });
      return newTabId;
    },
    [tabs, preferences, savePrefsMutation, defaultMainName, defaultNewName, captureCurrentVisibleItems, buildDefaultLayoutForNewTab]
  );

  const renameTab = useCallback(
    async (tabId: string, newName: string): Promise<void> => {
      const trimmed = newName.trim();
      if (!trimmed) return;
      const nextTabs = tabs.map((tabItem) =>
        tabItem.id === tabId
          ? { ...tabItem, name: trimmed, updatedAt: new Date().toISOString() }
          : tabItem
      );
      await savePrefsMutation.mutateAsync({ tabs: nextTabs });
    },
    [tabs, savePrefsMutation]
  );

  const deleteTab = useCallback(
    async (tabId: string): Promise<void> => {
      if (tabs.length <= 1) {
        // Don't allow deleting the last tab — keeps the UX simple.
        toast({
          title: t('dashboards.tabs.cannotDeleteLast', 'Você precisa de pelo menos uma aba.'),
        });
        return;
      }
      const nextTabs = tabs.filter((tabItem) => tabItem.id !== tabId);
      const nextCurrent =
        currentTabId === tabId
          ? (nextTabs[0]?.id ?? null)
          : currentTabId ?? null;
      await savePrefsMutation.mutateAsync({ tabs: nextTabs, currentTabId: nextCurrent });
    },
    [tabs, currentTabId, savePrefsMutation, toast, t]
  );

  const duplicateTab = useCallback(
    async (tabId: string, newName?: string): Promise<string | undefined> => {
      const source = tabs.find((tabItem) => tabItem.id === tabId);
      if (!source) return undefined;
      const now = new Date().toISOString();
      const cloneId = uuidv4();
      const baseName =
        typeof source.name === 'string'
          ? source.name
          : source.name?.[i18n.language] ?? source.name?.en ?? defaultMainName();
      const cloneName =
        newName ?? `${baseName} ${t('dashboards.tabs.copySuffix', '(cópia)')}`;
      const clone: DashboardTab = {
        id: cloneId,
        name: cloneName,
        items: source.items.map((i) => ({ ...i })),
        gridColumns: source.gridColumns,
        layouts: source.layouts?.map((l) => ({ ...l })),
        currentLayoutId: source.currentLayoutId,
        createdAt: now,
        updatedAt: now,
      };
      const nextTabs = [...tabs, clone];
      await savePrefsMutation.mutateAsync({ tabs: nextTabs, currentTabId: cloneId });
      return cloneId;
    },
    [tabs, savePrefsMutation, i18n.language, t, defaultMainName]
  );

  return {
    tabs,
    currentTabId: currentTab?.id,
    currentTab,
    isLoading,
    isSwitching: savePrefsMutation.isPending,
    hasTabs,
    setActiveTab,
    addTab,
    renameTab,
    deleteTab,
    duplicateTab,
  };
};
